// Leaderboard client (Pages Functions in functions/api). Nickname-only: a random device id, never
// shown, keeps one best run per environment. Live in production builds, or with ?lb in dev (so local
// play and tests never hit a missing API).
import { SIM } from '../config/tuning';
import type { VehicleId } from '../config/vehicles';
import type { GhostRecord } from '../sim/ghostFormat';
import type { UpgradeLevels } from '../sim/upgrades';
import { cleanName } from './names';

export interface BoardRow {
  rank: number;
  name: string;
  tag: string;
  distance: number;
  vehicle: VehicleId;
  ghost: boolean;
  mine: boolean;
}

export interface Board {
  env: string;
  top: BoardRow[];
  me: { rank: number; best: number; vehicle: VehicleId } | null;
  total: number;
}

export interface Submission {
  envId: string;
  distance: number;
  coins: number;
  ticks: number;
  vehicle: VehicleId;
  levels: UpgradeLevels;
  ghost: GhostRecord;
}

const ID_KEY = 'mbg-climb:player-id';
const NAME_KEY = 'mbg-climb:name';
const SENT_KEY = 'mbg-climb:submitted';

export const leaderboardEnabled = import.meta.env.PROD || new URLSearchParams(window.location.search).has('lb');

function storage(key: string, value?: string): string | null {
  try {
    if (value !== undefined) window.localStorage.setItem(key, value);
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** The device id: random, made once, never shown to anyone. */
export function playerId(): string {
  let id = storage(ID_KEY);
  if (!id || !/^[A-Za-z0-9-]{8,64}$/.test(id)) {
    id = crypto.randomUUID?.() ?? `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    storage(ID_KEY, id);
  }
  return id;
}

export function playerName(): string | null {
  return cleanName(storage(NAME_KEY) ?? '');
}

/** Best distance already on the server per environment (so only improvements are sent). */
function submitted(): Record<string, number> {
  try {
    const v = JSON.parse(storage(SENT_KEY) ?? '{}');
    return v && typeof v === 'object' ? (v as Record<string, number>) : {};
  } catch {
    return {};
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${import.meta.env.BASE_URL}api/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
  return body;
}

/** Saves the driver name on this device (required before playing). Returns the cleaned name or null. */
export function saveLocalName(name: string): string | null {
  const clean = cleanName(name);
  if (clean) storage(NAME_KEY, clean);
  return clean;
}

/** Sets (or changes) the nickname on the server; returns the cleaned name. */
export async function setName(name: string): Promise<string> {
  const clean = cleanName(name);
  if (!clean) throw new Error('bad name');
  const res = await call<{ name: string }>('name', { method: 'POST', body: JSON.stringify({ playerId: playerId(), name: clean }) });
  storage(NAME_KEY, res.name);
  return res.name;
}

/** Sends a run if it beats this device's stored best for the environment. Null when skipped. */
export async function submit(run: Submission): Promise<{ rank: number; total: number; best: number; improved: boolean } | null> {
  const name = playerName();
  if (!leaderboardEnabled || !name) return null;
  const sent = submitted();
  if (run.distance <= (sent[run.envId] ?? 0)) return null;
  const res = await call<{ rank: number; total: number; best: number; improved: boolean }>('scores', {
    method: 'POST',
    body: JSON.stringify({ playerId: playerId(), name, simVersion: SIM.version, ...run }),
  });
  storage(SENT_KEY, JSON.stringify({ ...sent, [run.envId]: res.best }));
  return res;
}

export function fetchBoard(envId: string): Promise<Board> {
  return call<Board>(`leaderboard?env=${encodeURIComponent(envId)}&playerId=${encodeURIComponent(playerId())}`);
}

/** The ghost of a ranked run (rank 1 = the world record), to race against. */
export async function fetchGhost(envId: string, rank = 1): Promise<{ name: string; tag: string; distance: number; ghost: GhostRecord }> {
  return call(`ghost?env=${encodeURIComponent(envId)}&rank=${rank}`);
}
